import React, { useContext, useState, useEffect } from "react";
import { Box, ButtonGroup, Button, TextField } from "@mui/material";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import '../styles/style.scss';
import { ThemeContext } from "../../context/ThemeContext";
import { OneClickTrading } from "../../api/request/dashboard/dashboard";
import toast from "react-hot-toast";
import { useSelector, shallowEqual } from "react-redux";
import { useSymbolPrices } from "../../context/WebSocketContext";

const OneClick = ({ open, onClose, symbolName }) => {
    const { darkMode } = useContext(ThemeContext);
    const { accountId: accountID } = useSelector(state => state.accountSummary)
    const symbols = useSymbolPrices();
    const allSymbolParams = useSelector((state) => state.symbol.allSymbolParams, shallowEqual);
    const { oneClickSymbolDetail = {} } = useSelector((state) => state.globalSymbol)
    const symbolId = oneClickSymbolDetail?.id;
    const digits = oneClickSymbolDetail?.digits


    const symbolData = symbols[symbolId] || {};
    const bidPrice = symbolData?.bid || 0;
    const askPrice = symbolData?.ask || 0;
    const symbolParameterData = symbolId ? allSymbolParams[symbolId] : {};

    const [value, setValue] = useState(symbolParameterData?.minimumAmountPerDeal);

    const handleChangeVolume = (inputValue) => {
        let formattedValue = parseFloat(inputValue).toFixed(2);
    
        if (isNaN(formattedValue)) {
            formattedValue = symbolParameterData?.minimumAmountPerDeal.toFixed(2);
        }
    
        if (
            formattedValue >= symbolParameterData?.minimumAmountPerDeal &&
            formattedValue <= symbolParameterData?.maximumAmountPerDeal
        ) {
            setValue(formattedValue);
        }
    };
    
    const handleValueChange = (isIncrease) => {
        setValue((prev) => {
            const step = symbolParameterData?.minimumAmountPerDeal || 0.1;
            let newValue = isIncrease ? parseFloat(prev) + step : parseFloat(prev) - step;
            newValue = parseFloat(newValue.toFixed(2)); 
    
            return newValue >= symbolParameterData?.minimumAmountPerDeal &&
                newValue <= symbolParameterData?.maximumAmountPerDeal
                ? newValue
                : prev;
        });
    };
    

    const handleTrade = async (side) => {
        console.log('calling with side ', side);

        if (!symbolId) {
            console.error("Symbol ID is missing.");
            return;
        }

        if (value < symbolParameterData?.minimumAmountPerDeal || value > symbolParameterData?.maximumAmountPerDeal) {
            // console.error("Trade volume is out of allowed limits.");
            toast.error("Trade volume is out of allowed limits.");
            return;
        }

        try {
            const payload = {
                accountId: Number(accountID),
                side,
                amount: value,
                symbolId,
                comment: "One-click trading",
            };

            const response = await OneClickTrading(payload);
            if (response?.data?.id) {
                const tradeId = response.data.id;
                const symbolName = symbols[symbolId]?.name;
                const price = side === 1 ? symbols[symbolId]?.ask : symbols[symbolId]?.bid;
                const tradeSide = side === 1 ? "BUY" : "SELL";

                toast.success(`Trade Opened: ID #${tradeId}, ${symbolName} at ${price ? price.toFixed(symbols[symbolId]?.digits) : "0.00"}, ${tradeSide}, Amount: ${value}`);
            }
            // toast.success(`Trade ${side === -1 ? "SELL" : "BUY"} created successfully!`);
            // console.log(`Trade executed: ${side === -1 ? "SELL" : "BUY"}`, response.data);
        } catch (error) {
            console.error("Trade execution error:", error);
            toast.error(`Trade execution failed`);
        }
    };

    const formatPrice = (price) => {
        return price ? price?.toFixed(digits) : "-";
    };
    if (!open) return null;

    return (
        <Box sx={{ position: 'absolute', padding: 12 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                <Box>
                    <ButtonGroup variant="" aria-label="sell group" sx={{ height: '25px' }} onClick={() => handleTrade(-1)}>
                        <Button sx={{ backgroundColor: symbolData?.bidColor === 'down' ? '#da5244' : symbolData?.bidColor === 'up' ? '#079a80' : 'inherit', color: "white", paddingRight: 1, borderRadius: 0, paddingLeft: 1 }}>{formatPrice(bidPrice) || "0.00"}</Button>
                        <Button sx={{ backgroundColor: symbolData?.bidColor === 'down' ? '#da5244' : symbolData?.bidColor === 'up' ? '#079a80' : 'inherit', color: "white", padding: '3px', px: 1, borderRadius: 0 }} >SELL</Button>
                    </ButtonGroup>

                </Box>

                <Box sx={{ marginTop: '1px', px: 1, display: "flex",justifyContent:'space-between', alignItems: "center", gap: 0.5, width: 110, height: '25px', backgroundColor: darkMode ? '#2e3338' : 'var(--white)', p: 1, border: `1px solid ${darkMode ? "var(--borderDark)" : 'var(--borderLight)'} ` }}>
                    <ArrowUpwardIcon onClick={() => handleValueChange(true)} sx={{ cursor: 'pointer', fontSize: '16px', color: darkMode ? 'white' : "gray" }} />
                    <TextField value={value} variant="filled" onChange={(e) => handleChangeVolume(e.target.value)} type='number'
                        InputProps={{
                            disableUnderline: true,
                            inputProps: {
                                min: symbolParameterData?.minimumAmountPerDeal,
                                max: symbolParameterData?.maximumAmountPerDeal,
                                step: symbolParameterData?.minimumAmountPerDeal
                            }
                        }}
                        sx={{ '& .MuiFilledInput-root': { backgroundColor: 'transparent' , fontSize: '13px', }, '& .MuiFilledInput-input': { padding: 0, px: 1, borderRadius: 0, backgroundColor: 'transparent', '&:focus': { backgroundColor: 'transparent' } } }}
                    />
                    <ArrowDownwardIcon onClick={() => handleValueChange(false)} sx={{ cursor: 'pointer', fontSize: '16px', color: darkMode ? 'white' : "gray" }} />
                </Box>
                <Box>
                    <ButtonGroup variant="" aria-label="buy group" sx={{ height: '25px' }}  onClick={() => handleTrade(1)}>
                        <Button sx={{ backgroundColor: symbolData?.askColor === 'down' ? '#da5244' : symbolData?.askColor === 'up' ? '#079a80' : 'inherit', color: "white", padding: '3px', px: 1, borderRadius: 0 }} >BUY</Button>
                        <Button sx={{ backgroundColor: symbolData?.askColor === 'down' ? '#da5244' : symbolData?.askColor === 'up' ? '#079a80' : 'inherit', color: "white", paddingRight: 1, borderRadius: 0, paddingLeft: 1 }} >{formatPrice(askPrice) || "0.00"}</Button>
                    </ButtonGroup>
                </Box>
            </Box>
        </Box>
    );
};

export default OneClick;
